/**
 * Moteur du test « shadowban LinkedIn ».
 *
 * LinkedIn ne documente aucun « shadowban » sous ce nom. Ce qu'il documente :
 * - les Règles de la communauté professionnelle : la visibilité d'un contenu
 *   peut être limitée, étiquetée ou retirée ; les infractions répétées ou
 *   graves peuvent mener à la restriction du compte ;
 * - la page d'aide sur les restrictions de compte (a1340522) : contenu,
 *   profil, identité, et outils automatiques (logiciels tiers et extensions qui
 *   extraient, modifient ou automatisent l'activité) ; restriction temporaire
 *   ou définitive, réexamen depuis l'écran de connexion ;
 * - la page sur les logiciels et extensions interdits (a1341387).
 * Le reste (poids des liens, seuils) vient des études citées dans nos articles
 * `mettre-un-lien-dans-un-post-linkedin` et `premiere-heure-post-linkedin`.
 *
 * Le moteur ne tranche donc jamais « oui, vous êtes shadowban » : il classe des
 * hypothèses par force d'indice et dit pour chacune si LinkedIn la documente.
 */

export type Notice = 'restriction' | 'visibility' | 'none' | 'unknown';
export type Pattern = 'sudden' | 'always' | 'one_post' | 'unknown';
export type Tool = 'extension' | 'agent' | 'pod' | 'bought';
export type AccountType = 'personal' | 'company';
export type LinkUse = 'most' | 'some' | 'none' | 'unknown';
export type FirstHour = 'zero' | 'few' | 'several' | 'unknown';

export interface ShadowbanInput {
  notice: Notice;
  pattern: Pattern;
  /** Vide = aucun outil (ou réponse inconnue). */
  tools: Tool[];
  account: AccountType;
  links: LinkUse;
  firstHour: FirstHour;
}

export type CauseId =
  | 'restriction'
  | 'content_limit'
  | 'automation'
  | 'artificial'
  | 'links'
  | 'momentum'
  | 'variance';

export type Source = 'documented' | 'studies' | 'mechanism';

export interface Cause {
  id: CauseId;
  score: number;
  title: string;
  /** Pourquoi cette cause ressort dans CE cas, construit depuis les réponses. */
  why: string;
  source: Source;
  action: string;
}

export interface ShadowbanResult {
  headline: string;
  causes: Cause[];
  /** Actions dédupliquées, dans l'ordre : ce qui expose le compte passe avant ce qui améliore la portée. */
  actions: string[];
  caveat: string;
}

export const SOURCE_LABEL: Record<Source, string> = {
  documented: 'Documenté par LinkedIn',
  studies: 'Constaté dans des études, pas par LinkedIn',
  mechanism: 'Mécanisme de diffusion, non chiffré par LinkedIn',
};

const clamp = (n: number) => Math.max(0, Math.min(100, n));

export function diagnoseShadowban(input: ShadowbanInput): ShadowbanResult {
  const { notice, pattern, tools, account, links, firstHour } = input;
  const causes: Cause[] = [];
  const hasAutomation = tools.includes('extension') || tools.includes('agent');
  const hasArtificial = tools.includes('pod') || tools.includes('bought');
  const patternBoost = pattern === 'sudden' ? 15 : pattern === 'always' ? 8 : 0;

  if (notice === 'restriction') {
    causes.push({
      id: 'restriction',
      score: 100,
      title: 'Votre compte est restreint, ce n’est pas un shadowban',
      why: 'Vous avez reçu un message de restriction : LinkedIn vous l’a dit, donc il n’y a rien à deviner. La page d’aide cite quatre causes : contenu, profil, identité, outils automatiques.',
      source: 'documented',
      action:
        'Connectez-vous et suivez les consignes à l’écran : un réexamen pour une restriction liée au contenu, une confirmation d’identité pour le profil ou l’identité. La page d’aide ne donne aucune durée.',
    });
  }

  if (notice === 'visibility') {
    causes.push({
      id: 'content_limit',
      score: 90,
      title: 'Un contenu a été limité, étiqueté ou retiré',
      why: 'Les Règles de la communauté prévoient exactement ça : selon la gravité, LinkedIn peut limiter la visibilité d’un contenu, l’étiqueter ou le retirer. Un avis sur un post précis est le signal le plus net que vous puissiez recevoir.',
      source: 'documented',
      action:
        'Relisez le post concerné contre les Règles de la communauté professionnelle, retirez-le s’il enfreint une règle, et ne le republiez pas tel quel.',
    });
  }

  if (hasAutomation) {
    const names = [
      tools.includes('extension') ? 'une extension d’automatisation ou d’extraction' : '',
      tools.includes('agent') ? 'un agent IA qui clique à votre place' : '',
    ]
      .filter(Boolean)
      .join(' et ');
    causes.push({
      id: 'automation',
      score: clamp(70 + patternBoost + (notice === 'restriction' ? 10 : 0)),
      title: 'Un outil qui automatise votre activité vous expose',
      why: `Vous utilisez ${names}. La règle de LinkedIn vise les logiciels, robots et extensions qui extraient des données, modifient le site ou automatisent l’activité, avec une restriction temporaire ou définitive à la clé. Elle ne dit pas comment ces outils sont repérés.`,
      source: 'documented',
      action:
        'Désactivez l’outil maintenant, avant de chercher ailleurs : tant qu’il tourne, rien d’autre ne se mesure correctement. Gardez les agents en lecture seule, c’est vous qui cliquez.',
    });
  }

  if (hasArtificial) {
    const names = [
      tools.includes('pod') ? 'un groupe où l’on s’engage à réagir aux posts des autres' : '',
      tools.includes('bought') ? 'de l’engagement acheté' : '',
    ]
      .filter(Boolean)
      .join(' et ');
    causes.push({
      id: 'artificial',
      score: clamp(60 + patternBoost),
      title: 'L’engagement artificiel est la piste que LinkedIn nomme',
      why: `Vous avez recours à ${names}. Les Règles interdisent d’augmenter artificiellement l’engagement et de convenir à l’avance de se liker ou repartager. La sanction la plus douce, une visibilité limitée, ne s’annonce par aucune notification : vous voyez seulement vos posts avancer moins.`,
      source: 'documented',
      action:
        'Quittez le groupe ou arrêtez l’achat, puis laissez passer quelques posts sans y toucher. La portée perdue ne revient pas par un signal, elle revient avec des réactions venues d’ailleurs.',
    });
  }

  if (links !== 'none' && links !== 'unknown') {
    const heavy = links === 'most';
    const company = account === 'company';
    const score = company ? (heavy ? 50 : 25) : heavy ? 25 : 10;
    causes.push({
      id: 'links',
      score: clamp(score + (pattern === 'always' ? 5 : 0)),
      title: 'Les liens dans le corps du post coûtent de la portée',
      why: company
        ? `Vous publiez depuis une page entreprise ${heavy ? 'avec un lien dans presque chaque post' : 'avec un lien dans certains posts'}. D’après la synthèse d’Ordinal, ce sont surtout les pages entreprise qui encaissent la pénalité de lien.`
        : `Vous publiez depuis un profil personnel ${heavy ? 'avec un lien dans presque chaque post' : 'avec un lien de temps en temps'}. Les études varient (de 13 % à 36 % de portée en moins), mais les profils personnels montrent presque aucune pénalité dans les données d’Ordinal : le lien est rarement l’explication d’une chute brutale.`,
      source: 'studies',
      action:
        'Testez trois posts sans lien dans le corps (lien en commentaire ou rien du tout) et comparez les impressions. Si rien ne bouge, le lien n’était pas la cause.',
    });
  }

  const momentumBase = firstHour === 'zero' ? 70 : firstHour === 'few' ? 40 : firstHour === 'several' ? 5 : 35;
  // Une restriction notifiée explique déjà la chute, et ce parcours court ne pose pas la question de la première heure.
  if (notice !== 'restriction') {
    causes.push({
      id: 'momentum',
      score: clamp(momentumBase + (pattern === 'one_post' ? 12 : pattern === 'always' ? 8 : 0)),
      title: 'Le post n’a pas eu de premier élan',
      why:
        firstHour === 'zero'
          ? 'Aucune réaction dans la première heure : LinkedIn teste un post sur un petit échantillon et n’élargit que si ce groupe réagit. C’est de loin la cause la plus fréquente d’une portée basse, sans aucune restriction.'
          : firstHour === 'few'
            ? '1 ou 2 réactions dans la première heure, c’est mince pour déclencher un élargissement. Cela suffit à expliquer une portée basse sans restriction.'
            : firstHour === 'several'
              ? '3 réactions ou plus dans la première heure : le post a passé le test de départ, donc ce n’est probablement pas ici que ça coince.'
              : 'Vous ne savez pas combien de réactions sont venues dans la première heure. Sans cette donnée, on ne peut pas écarter cette cause, la plus fréquente.',
      source: 'mechanism',
      action:
        'Restez disponible 60 minutes après la publication et répondez à chaque commentaire. Notez pour les trois prochains posts combien de réactions arrivent dans l’heure.',
    });
  }

  if (pattern === 'one_post') {
    causes.push({
      id: 'variance',
      score: 35,
      title: 'Un seul post faible n’est pas un signal',
      why: 'Un post isolé en dessous des autres est la variation normale de la portée, pas une restriction. Un shadowban supposé se verrait sur plusieurs posts d’affilée.',
      source: 'mechanism',
      action: 'Attendez trois à cinq posts avant de conclure quoi que ce soit.',
    });
  }

  const ranked = causes.sort((a, b) => b.score - a.score).filter((c) => c.score >= 30).slice(0, 3);

  const caveat =
    'LinkedIn ne publie aucun test de shadowban et ne confirme pas ce terme. Ce résultat classe des hypothèses d’après vos réponses ; il n’accède pas à votre compte. Pour un chiffre fiable, comparez les impressions de vos derniers posts dans leurs statistiques.';

  if (ranked.length === 0) {
    return {
      headline: 'Rien ne pointe vers une restriction dans vos réponses.',
      causes: [],
      actions: [
        'Comparez les impressions de vos cinq derniers posts dans leurs statistiques : une chute réelle se voit sur plusieurs posts, pas sur un.',
        'Vérifiez que le post est bien visible : ouvrez son lien depuis une fenêtre de navigation privée ou un autre compte.',
        'Sans outil, sans avis de LinkedIn et avec un bon départ, le plus probable est une variation de la portée.',
      ],
      caveat,
    };
  }

  const top = ranked[0];
  const headline =
    top.id === 'restriction'
      ? 'Votre compte est restreint : LinkedIn vous l’a dit.'
      : top.id === 'content_limit'
        ? 'Un contenu a été limité : c’est documenté, et l’avis le prouve.'
        : top.id === 'automation' || top.id === 'artificial'
          ? 'La piste la plus sérieuse : un comportement que LinkedIn interdit.'
          : top.id === 'momentum'
            ? 'Probablement pas un shadowban : vos posts manquent de premier élan.'
            : top.id === 'links'
              ? 'Le lien dans le corps du post est la piste la plus probable.'
              : 'Un seul post faible : pas de quoi parler de shadowban.';

  const actions = Array.from(new Set(ranked.map((c) => c.action)));
  return { headline, causes: ranked, actions, caveat };
}
